import { Component, ErrorInfo, ReactNode } from "react";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Error caught by boundary:", error, errorInfo);

    // Send error to monitoring service
    if (import.meta.env.PROD) {
      // Integration with error monitoring service
    }
  }

  public render() {
    if (this.state.hasError) {
      return (
        this.props.fallback || (
          <div className="error-fallback">
            <div className="min-h-screen flex flex-col items-center justify-center bg-white text-center px-6 py-12">
              <div className="text-6xl mb-4 animate-bounce">🚧</div>

              <h1 className="text-4xl font-extrabold bg-gradient-primary bg-clip-text text-transparent mb-2">
                Oops! Something broke.
              </h1>

              <p className="text-gray-600 text-lg max-w-md mb-6">
                Our systems tripped over a wire. It's not you, it's us.
                <br />
                Try refreshing the page — we’ll be right back on track.
              </p>

              <button
                onClick={() => window.location.reload()}
                className="px-6 py-3 rounded-lg bg-gradient-to-r from-indigo-500 to-purple-500 text-white font-medium shadow-lg hover:scale-105 transition-transform duration-200"
              >
                🔄 Refresh Page
              </button>

              <p className="text-sm text-gray-400 mt-6">
                If this keeps happening, contact support with the steps that
                caused this.
              </p>
            </div>
          </div>
        )
      );
    }

    return this.props.children;
  }
}
